import { termsForDimension, contrastTextColor } from './label-style'
import type { ExploreProperty } from '../../widgets/explorer/data'
import type { TierDisplay, TierPlacement, TierRatingConfig, TierRow } from '../../widgets/explorer/tier-rating/schema'

/**
 * Tier Rating (#1813) resolver — pure. Mirrors the toolkit
 * `Settings::match()` (first configured row, in rank order, whose term the
 * home carries) and `Settings::tier_payload()` (the public tier object both
 * renderers draw from). Terms come from the card payload's own per-dimension
 * `{id,name}[]` lists (central#2615), read through the Label Designer's
 * `termsForDimension` so singular relations (type / city) wrap the same way.
 */
export type CardTier = {
  /** CSS-safe handle for the `hrtk-tier--{slug}` class: from the label, like the toolkit's term slug. */
  slug: string
  label: string
  /** 1-based rank in the ladder (1 = top tier). */
  rank: number
  stars: number
  image: string
  color: string
  description: string
}

/** Null until at least one tier row exists — the no-config branch keeps today's card byte-for-byte. */
export function readTierConfig(card: { tierRating?: TierRatingConfig | null } | undefined): TierRatingConfig | null {
  const cfg = card?.tierRating
  if (!cfg || !Array.isArray(cfg.tiers) || cfg.tiers.length === 0) return null
  return cfg
}

export function tierDisplay(cfg: TierRatingConfig | null | undefined): TierDisplay {
  return cfg?.display ?? 'both'
}

export function tierPlacement(cfg: TierRatingConfig | null | undefined): TierPlacement {
  return cfg?.placement ?? 'title'
}

/** The popover's resolved copy; every field '' when unset. */
export type TierPopoverConfig = { heading: string; intro: string; linkText: string; linkUrl: string }

/**
 * Popover config (#1821), or null when the operator switched it off. Toolkit
 * default is ON (`Settings::defaults()['popover']['enabled'] = true`), so an
 * absent group opens a popover with the tier name and description alone.
 */
export function tierPopover(cfg: TierRatingConfig | null | undefined): TierPopoverConfig | null {
  const p = cfg?.popover
  if (p?.enabled === false) return null
  return { heading: p?.heading ?? '', intro: p?.intro ?? '', linkText: p?.linkText ?? '', linkUrl: p?.linkUrl ?? '' }
}

/** `sanitize_html_class` + lowercase, as the toolkit's `cssClass()` does for the badge slug. */
export function tierSlug(label: string): string {
  const slug = label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9_-]/g, '')
  return slug || 'tier'
}

/** The configured tier a home falls in, or null. */
export function resolveCardTier(property: ExploreProperty, cfg: TierRatingConfig | null): CardTier | null {
  const rows = cfg?.tiers
  if (!rows || rows.length === 0) return null
  for (let i = 0; i < rows.length; i++) {
    const row: TierRow = rows[i]
    const term = termsForDimension(property, row.filterKey).find((t) => t.id === row.centralId)
    if (!term) continue
    const label = (row.label ?? '').trim() || (term.name ?? '').trim()
    if (!label) continue
    return {
      slug: tierSlug(label),
      label,
      rank: i + 1,
      stars: Math.max(0, Math.min(5, row.stars ?? 0)),
      image: row.imageUrl ?? '',
      color: row.color ?? '',
      description: row.description ?? '',
    }
  }
  return null
}

/**
 * Per-tier tint as the `--hrtk-tier-*` custom properties the toolkit's
 * `Renderer::css()` prints per slug — inline on the badge root here, so no
 * generated stylesheet. '' tint = no style (the neutral badge).
 */
export function tierTintStyle(color: string): Record<string, string> | undefined {
  const c = color.trim()
  if (!c) return undefined
  // 8-digit hexa: contrast is measured on the opaque RGB part.
  const fg = contrastTextColor(c.length === 9 ? c.slice(0, 7) : c)
  // Stars follow the text colour on a tint: the default gold star would vanish
  // on a gold tier (the one tint a rating badge is most likely to wear).
  return { '--hrtk-tier-bg': c, '--hrtk-tier-fg': fg, '--hrtk-tier-border': c, '--hrtk-tier-star': fg }
}
